"use strict";

import { baseUrl, handleRedirect } from "@/modules/configuration.js";
import hostedFieldsPopupFixture from "@/fixtures/tranzila/hosted_fields_popup.json";

const HF_SCRIPT_URL = "https://hf.tranzila.com/assets/js/thostedf.js";
const APPLE_SCRIPT_URL = "https://hf.tranzila.com/assets/js/thfapplepay.js";

export const TRANZILA_OVERLAY_CHANGE_EVENT = "yummi-tranzila-overlay-change";

export const SESSION_EXPIRED_HE = "פג תוקף התשלום, נא לנסות שוב";
export const HANDSHAKE_RETRY_HE =
  "פג תוקף תהליך התשלום. לחצו שוב על כפתור התשלום לקבלת קוד חדש.";
export const MISSING_FIELDS_HE =
  "לא ניתן להתחיל תשלום — נתונים חסרים. נא לנסות שוב.";
export const CONTACT_SUPPORT_HE =
  "התשלום נקלט אצל חברת האשראי אך לא אושר במערכת. נא לפנות לשירות הלקוחות.";
export const PAYMENT_VERIFYING_HE =
  "מאמתים את התשלום מול חברת האשראי, נא לא לסגור את החלון...";
export const CONFIRM_FAILED_HE = "אירעה שגיאה באישור התשלום. נא לפנות לשירות הלקוחות ולא לשלם שוב.";
export const CHARGE_FAILED_HE = "התשלום נכשל";
export const METHOD_NOT_ALLOWED_HE =
  "לא ניתן לשלם בשיטה שנבחרה. לחצו שוב על כפתור התשלום.";
export const BRAND_NOT_CLEARED_HE = "מסוף לא מורשה לסלוק את המותג שבעסקה";
export const AMEX_DINERS_UNAVAILABLE_HE =
  "זמנית לא ניתן לשלם בכרטיסי אשראי מסוג אמריקן אקספרס או דיינרס.";

const OPTIONAL_HOSTED_FIELDS_KEYS = ["requested_by_user", "DCdisable", "notify_url"];

export const SELECTED_METHODS = ["card", "bit", "apple", "google"];

export const REQUIRED_HOSTED_FIELDS_KEYS = Object.keys(
  hostedFieldsPopupFixture.form.hosted_fields
).filter((key) => !OPTIONAL_HOSTED_FIELDS_KEYS.includes(key));

const METHOD_KEYS = ["card", "bit", "apple", "google"];

// TEMP: set to true to send notify_url to Tranzila again (IPN callback).
const SEND_TRANZILA_NOTIFY_URL = true;

const CHARGE_PASSTHROUGH_KEYS = [
  "thtk",
  "terminal_name",
  "amount",
  "currency_code",
  "tran_mode",
  "notify_url",
  "tokenize",
  "requested_by_user",
  "response_language",
  "pdesc",
  "contact",
  "email",
  "card_holder_name",
  "card_holder_email",
  "phone_country_code",
  "phone_number",
  "force_txn_on_3ds_fail",
  "force_challenge",
  "DCdisable",
];

let consumedThtk = null;
let chargeSucceeded = false;

export function dispatchTranzilaOverlayChange(open) {
  if (typeof document === "undefined") {
    return;
  }
  document.dispatchEvent(
    new CustomEvent(TRANZILA_OVERLAY_CHANGE_EVENT, { detail: { open: Boolean(open) } })
  );
}

export function isHandshakeExpired(hostedFields) {
  const raw = hostedFields?.handshake_expires_at;
  if (!raw) {
    return true;
  }
  const expires = Date.parse(raw);
  if (Number.isNaN(expires)) {
    return true;
  }
  return Date.now() >= expires;
}

export function remainingHandshakeMs(hostedFields, pageTimout) {
  const fallback = Number(pageTimout) > 0 ? Number(pageTimout) : 240000;
  const expires = Date.parse(hostedFields?.handshake_expires_at);
  if (Number.isNaN(expires)) {
    return fallback;
  }
  const untilHandshake = Math.max(0, expires - Date.now());
  return Math.min(fallback, untilHandshake);
}

function isPresent(value) {
  if (value === false || value === 0) {
    return true;
  }
  if (value == null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  return true;
}

export function missingHostedFieldsKeys(hostedFields) {
  if (!hostedFields || typeof hostedFields !== "object") {
    return REQUIRED_HOSTED_FIELDS_KEYS.slice();
  }
  const missing = [];
  for (const key of REQUIRED_HOSTED_FIELDS_KEYS) {
    if (!isPresent(hostedFields[key])) {
      missing.push(key);
    }
  }
  const methods = hostedFields.methods;
  if (!methods || typeof methods !== "object") {
    if (!missing.includes("methods")) {
      missing.push("methods");
    }
  } else {
    for (const method of METHOD_KEYS) {
      if (typeof methods[method] !== "boolean") {
        missing.push(`methods.${method}`);
      }
    }
  }
  const selected = hostedFields.selected_method;
  if (selected && !SELECTED_METHODS.includes(selected)) {
    missing.push("selected_method");
  }
  return missing;
}

export function selectedMethodAllowed(hostedFields) {
  const selected = hostedFields?.selected_method;
  if (!SELECTED_METHODS.includes(selected)) {
    return false;
  }
  return hostedFields?.methods?.[selected] === true;
}

export function canChargeHostedFields(hostedFields) {
  return (
    missingHostedFieldsKeys(hostedFields).length === 0 &&
    selectedMethodAllowed(hostedFields) &&
    !isHandshakeExpired(hostedFields)
  );
}

export function isThtkConsumed(thtk) {
  const code = String(thtk || "").trim();
  return Boolean(code && consumedThtk && code === consumedThtk);
}

export function invalidateThtk(thtk) {
  const code = String(thtk || "").trim();
  if (code) {
    consumedThtk = code;
  }
}

export function markChargeSucceeded() {
  chargeSucceeded = true;
}

export function hasChargeSucceeded() {
  return chargeSucceeded;
}

export function resetTranzilaChargeSession() {
  chargeSucceeded = false;
}

export function teardownTranzilaSession(thtk) {
  dispatchTranzilaOverlayChange(false);
  if (thtk) {
    invalidateThtk(thtk);
  }
}

export function buildChargePayload(hostedFields) {
  const payload = {};
  for (const key of CHARGE_PASSTHROUGH_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(hostedFields, key)) {
      continue;
    }
    const value = hostedFields[key];
    if (value === undefined) {
      continue;
    }
    if (key === "requested_by_user" && (value == null || String(value).trim() === "")) {
      continue;
    }
    if (key === "notify_url" && (!SEND_TRANZILA_NOTIFY_URL || !isPresent(value))) {
      continue;
    }
    payload[key] = value;
  }
  return payload;
}

export function chargeParamsForMethod(hostedFields, paymentMethod) {
  const payload = buildChargePayload(hostedFields);
  if (paymentMethod === "bit") {
    payload.currency_code = hostedFields.currency_code;
    payload.transaction_layout = "qr-sms";
    payload.bit_language = "hebrew";
  }
  if (paymentMethod === "apple") {
    payload.labelApple = "Yummi";
  }
  return payload;
}

const GOOGLE_PAY_ENABLE_FLAG_KEYS = [
  "response",
  "result",
  "enabled",
  "enable",
  "isEnable",
  "isEnabled",
  "available",
  "isReadyToPay",
  "googleIsEnable",
];

function googlePayFlagSaysUnavailable(value) {
  if (value === false || value === 0) {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  const text = value.trim().toLowerCase();
  return text === "false" || text === "0" || text === "no" || text === "disabled" || text === "unavailable";
}

/** True when a googleIsEnable payload says the wallet cannot pay. A bare enable event is available. */
export function googlePayEventSaysUnavailable(event) {
  if (event == null || event === true) {
    return false;
  }
  if (googlePayFlagSaysUnavailable(event)) {
    return true;
  }
  if (typeof event === "string") {
    return /unavailable|not available|not enabled|disabled/i.test(event);
  }
  if (typeof event !== "object") {
    return false;
  }
  for (const key of GOOGLE_PAY_ENABLE_FLAG_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(event, key)) {
      continue;
    }
    const value = event[key];
    if (googlePayFlagSaysUnavailable(value)) {
      return true;
    }
    if (!value || typeof value !== "object") {
      continue;
    }
    for (const nestedKey of GOOGLE_PAY_ENABLE_FLAG_KEYS) {
      if (googlePayFlagSaysUnavailable(value[nestedKey])) {
        return true;
      }
    }
  }
  const message = typeof event.message === "string" ? event.message : typeof event.error === "string" ? event.error : "";
  return /unavailable|not available|not enabled/i.test(message);
}

export function popupChargeError(message) {
  const text = typeof message === "string" ? message.trim() : "";
  if (text === BRAND_NOT_CLEARED_HE) {
    return AMEX_DINERS_UNAVAILABLE_HE;
  }
  return text;
}

export function isPayableChargeResponse(response) {
  const txn = response?.transaction_response;
  if (!txn || txn.success !== true) {
    return false;
  }
  if (Object.prototype.hasOwnProperty.call(txn, "processor_response_code")) {
    return txn.processor_response_code === "000";
  }
  return true;
}

// Fixed Yummi path. Not a hosted_fields key and never sent to Tranzila.
const TRANZILA_CLIENT_ERROR_PATH = "/orders/tranzila_client_error";

const CLIENT_ERROR_REDACT_KEYS = new Set([
  "card_holder_id_number",
  "identity_number",
  "credit_card_holder_id",
  "card_holder_id",
  "cardholderid",
  "credit_card_number",
  "card_number",
  "pan",
  "cvv",
  "cvc",
  "expiry",
  "expiry_month",
  "expiry_year",
  "token",
]);

const CUSTOMER_CANCEL_CODES = new Set([
  "canceled",
  "cancelled",
  "user_canceled",
  "user_cancelled",
  "buyer_canceled",
  "buyer_cancelled",
]);

function isRedactedClientErrorKey(key) {
  return CLIENT_ERROR_REDACT_KEYS.has(String(key).toLowerCase());
}

function redactClientErrorValue(value, seen = new WeakSet()) {
  if (Array.isArray(value)) {
    return value.map((item) => redactClientErrorValue(item, seen));
  }
  if (!value || typeof value !== "object") {
    return value;
  }
  if (seen.has(value)) {
    return null;
  }
  seen.add(value);
  const out = {};
  for (const [key, child] of Object.entries(value)) {
    if (isRedactedClientErrorKey(key)) {
      continue;
    }
    out[key] = redactClientErrorValue(child, seen);
  }
  return out;
}

function clientErrorMessage(err, txn) {
  if (err) {
    if (typeof err === "string") {
      return err;
    }
    if (err.message) {
      return String(err.message);
    }
    try {
      return JSON.stringify(redactClientErrorValue(err));
    } catch {
      return "";
    }
  }
  if (typeof txn?.error === "string") {
    return txn.error;
  }
  return "";
}

function cancellationTexts(err, response) {
  const texts = [];
  if (typeof err === "string") {
    texts.push(err);
  }
  if (typeof err?.message === "string") {
    texts.push(err.message);
  }
  if (Array.isArray(err?.messages)) {
    for (const item of err.messages) {
      if (typeof item === "string") {
        texts.push(item);
      } else if (typeof item?.message === "string") {
        texts.push(item.message);
      }
    }
  }
  const txnError = response?.transaction_response?.error;
  if (typeof txnError === "string") {
    texts.push(txnError);
  }
  return texts;
}

/** True only for an explicit customer cancel. Anything unclear stays reportable. */
export function isCustomerPaymentCancellation(err, response) {
  try {
    if (err?.cancelled === true || err?.canceled === true) {
      return true;
    }
    if (err?.user_cancelled === true || err?.user_canceled === true) {
      return true;
    }
    const txn = response?.transaction_response;
    if (txn?.cancelled === true || txn?.canceled === true) {
      return true;
    }
    const code = String(
      err?.error_code ?? err?.code ?? err?.statusCode ?? err?.status ?? txn?.error_code ?? ""
    )
      .trim()
      .toLowerCase();
    if (CUSTOMER_CANCEL_CODES.has(code)) {
      return true;
    }
    return cancellationTexts(err, response).some((text) =>
      /user cancell?ed|cancell?ed by (the )?(user|customer|buyer)|buyer cancell?ed|sheet (was )?closed/i.test(
        text
      )
    );
  } catch {
    return false;
  }
}

export function reportTranzilaClientError(hostedFields, details) {
  try {
    const { stage, paymentMethod, err, response } = details || {};
    const txn = response?.transaction_response || {};
    const message = clientErrorMessage(err, txn);
    const formData = new FormData();
    formData.append("flow", hostedFields?.flow || "");
    formData.append("stage", stage);
    formData.append("payment_method", paymentMethod || "");
    formData.append("message", String(message).slice(0, 300));
    formData.append("error_code", String(err?.error_code ?? err?.code ?? txn.error_code ?? ""));
    formData.append("processor_response_code", String(txn.processor_response_code ?? ""));
    formData.append("transaction_id", String(txn.transaction_id ?? ""));
    formData.append("thtk", hostedFields?.thtk || "");
    fetch(baseUrl(TRANZILA_CLIENT_ERROR_PATH), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
      body: formData,
    }).catch(() => {});
  } catch {
    // never break the payment flow because of reporting
  }
}

export function loadTranzilaHostedFields() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window is not available"));
  }
  if (window.__tranzilaHfPromise) {
    return window.__tranzilaHfPromise;
  }
  window.__tranzilaHfPromise = new Promise((resolve, reject) => {
    if (window.TzlaHostedFields) {
      resolve(window.TzlaHostedFields);
      return;
    }
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;
    s.src = HF_SCRIPT_URL;
    s.onload = () => {
      if (!window.TzlaHostedFields) {
        reject(new Error("TzlaHostedFields not available after script load"));
        return;
      }
      resolve(window.TzlaHostedFields);
    };
    s.onerror = () => reject(new Error("Failed to load Tranzila Hosted Fields script"));
    const x = document.getElementsByTagName("script")[0];
    if (x?.parentNode) {
      x.parentNode.insertBefore(s, x);
    } else {
      document.head.appendChild(s);
    }
  });
  return window.__tranzilaHfPromise;
}

export function loadTranzilaApplePay() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window is not available"));
  }
  if (window.__tranzilaApplePromise) {
    return window.__tranzilaApplePromise;
  }
  window.__tranzilaApplePromise = new Promise((resolve, reject) => {
    if (typeof window.hfChargeApple === "function") {
      resolve(window.hfChargeApple);
      return;
    }
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;
    s.src = APPLE_SCRIPT_URL;
    s.onload = () => {
      if (typeof window.hfChargeApple !== "function") {
        reject(new Error("hfChargeApple not available after script load"));
        return;
      }
      resolve(window.hfChargeApple);
    };
    s.onerror = () => reject(new Error("Failed to load Tranzila Apple Pay script"));
    const x = document.getElementsByTagName("script")[0];
    if (x?.parentNode) {
      x.parentNode.insertBefore(s, x);
    } else {
      document.head.appendChild(s);
    }
  });
  return window.__tranzilaApplePromise;
}

const CONFIRM_STRIP_KEYS = new Set([
  "card_holder_id_number",
  "identity_number",
  "credit_card_holder_id",
  "card_holder_id",
  "cardholderid",
]);

function shouldStripConfirmKey(key) {
  return CONFIRM_STRIP_KEYS.has(String(key).toLowerCase());
}

function stripCardHolderIdFromNode(node) {
  if (Array.isArray(node)) {
    return node.map(stripCardHolderIdFromNode);
  }
  if (node && typeof node === "object") {
    const out = {};
    for (const [key, value] of Object.entries(node)) {
      if (shouldStripConfirmKey(key)) {
        continue;
      }
      out[key] = stripCardHolderIdFromNode(value);
    }
    return out;
  }
  return node;
}

export function sanitizeTranzilaResponseForConfirm(tranzilaResponse) {
  if (tranzilaResponse == null) {
    return tranzilaResponse;
  }
  try {
    return stripCardHolderIdFromNode(JSON.parse(JSON.stringify(tranzilaResponse)));
  } catch {
    return stripCardHolderIdFromNode(tranzilaResponse);
  }
}

export async function confirmTranzilaPayment({ confirmUrl, flow, paymentMethod, tranzilaResponse }) {
  const url = baseUrl(confirmUrl || "/orders/tranzila_confirm");
  const formData = new FormData();
  formData.append("flow", flow);
  formData.append("payment_method", paymentMethod);
  formData.append("tranzila_response", JSON.stringify(sanitizeTranzilaResponseForConfirm(tranzilaResponse)));

  const response = await fetch(url, {
    method: "POST",
    redirect: "manual",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
    body: formData,
  });

  if (response.status === 404) {
    return { error: true, message: "Not found" };
  }
  if (response.status !== 200) {
    return { error: true, message: "Request failed" };
  }

  const data = await response.json();
  if (handleRedirect(data)) {
    return { redirected: true };
  }
  return data;
}

export function logTxnIdOnly(response) {
  const txnId = response?.transaction_response?.transaction_id;
  if (txnId != null) {
    console.info("Tranzila transaction_id", txnId);
  }
}

const TRANZILA_PENDING_CHARGE_KEY = "tranzila_pending_charge";
const DEFAULT_STATUS_POLL_INTERVAL_MS = 3000;
const DEFAULT_STATUS_POLL_TIMEOUT_MS = 120000;

function positiveMs(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return fallback;
  }
  return n;
}

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function readTranzilaPendingCharge() {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(TRANZILA_PENDING_CHARGE_KEY);
    if (!raw) {
      return null;
    }
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function clearTranzilaPendingCharge() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(TRANZILA_PENDING_CHARGE_KEY);
  } catch {
    /* ignore quota / private-mode failures */
  }
}

export function writeTranzilaPendingCharge(hostedFields, startedAt = Date.now()) {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  const statusUrl = typeof hostedFields?.status_url === "string" ? hostedFields.status_url.trim() : "";
  if (!statusUrl) {
    return null;
  }
  const expiresAt = Date.parse(hostedFields?.handshake_expires_at);
  if (!Number.isFinite(expiresAt)) {
    return null;
  }
  const marker = {
    flow: hostedFields.flow,
    status_url: statusUrl,
    interval_ms: positiveMs(hostedFields.status_poll_interval_ms, DEFAULT_STATUS_POLL_INTERVAL_MS),
    timeout_ms: positiveMs(hostedFields.status_poll_timeout_ms, DEFAULT_STATUS_POLL_TIMEOUT_MS),
    started_at: startedAt,
    expires_at: expiresAt,
  };
  try {
    sessionStorage.setItem(TRANZILA_PENDING_CHARGE_KEY, JSON.stringify(marker));
  } catch {
    return null;
  }
  return marker;
}

/** Marker for this flow that has not passed handshake expiry. Any other marker is removed. */
export function matchingTranzilaPendingCharge(flow) {
  const marker = readTranzilaPendingCharge();
  if (!marker) {
    return null;
  }
  const expiresAt = Number(marker.expires_at);
  const statusUrl = typeof marker.status_url === "string" ? marker.status_url.trim() : "";
  const fresh = Number.isFinite(expiresAt) && Date.now() < expiresAt;
  if (marker.flow !== flow || !fresh || !statusUrl) {
    clearTranzilaPendingCharge();
    return null;
  }
  return marker;
}

export async function fetchTranzilaPaymentStatus(statusUrl) {
  const url = typeof statusUrl === "string" ? statusUrl.trim() : "";
  if (!url) {
    return { pending: true };
  }
  try {
    const response = await fetch(baseUrl(url), {
      method: "GET",
      redirect: "manual",
      cache: "no-store",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });
    if (!response || response.status !== 200) {
      return { pending: true };
    }
    const data = await response.json();
    if (handleRedirect(data)) {
      return { redirected: true };
    }
    return { pending: true };
  } catch {
    return { pending: true };
  }
}

export async function pollTranzilaPaymentStatus({
  statusUrl,
  intervalMs,
  timeoutMs,
  startedAt,
  isCancelled,
} = {}) {
  const origin = Number(startedAt);
  const start = Number.isFinite(origin) && origin > 0 ? origin : Date.now();
  const deadline = start + positiveMs(timeoutMs, DEFAULT_STATUS_POLL_TIMEOUT_MS);
  const interval = positiveMs(intervalMs, DEFAULT_STATUS_POLL_INTERVAL_MS);

  while (Date.now() < deadline) {
    if (isCancelled?.()) {
      return { cancelled: true };
    }
    const status = await fetchTranzilaPaymentStatus(statusUrl);
    if (status?.redirected) {
      return { redirected: true };
    }
    if (isCancelled?.()) {
      return { cancelled: true };
    }
    const wait = Math.min(interval, deadline - Date.now());
    if (wait <= 0) {
      break;
    }
    await delay(wait);
  }
  if (isCancelled?.()) {
    return { cancelled: true };
  }
  return { timedOut: true };
}
